/**
 * TabularTab — Expandable table with KPI data groups + advanced filter modal.
 *
 * Renders the same table structure as the original school.tsx:
 *  - "All" row with combined data
 *  - Per-group expandable rows
 *  - SchoolDetailsTable inside expanded rows
 *  - Default state-list table when no KPI is selected
 */

import React from 'react';
import SchoolDetailsTable from '../SchoolDetailsTable';
import { KVSLoader } from '../../../../components/loaders';
import { applyFilters } from '../../utils/filterUtils';
import { formatCellValue } from '../../utils/formatCellValue';
import type { UseFiltersReturn } from '../../hooks/useFilters';
import type { UseKPIDataReturn } from '../../hooks/useKPIData';
import type { CategoryConfig } from '../../categories/CategoryConfig';

interface TabularTabProps {
  filters: UseFiltersReturn;
  kpiData: UseKPIDataReturn;
}

export default function TabularTab({ filters, kpiData }: TabularTabProps) {
  const {
    state, region, category, kpi, selectedInfraItems,
    activeQuickFilters, globalFilters,
  } = filters;

  const {
    categoryList, kpiList,
    kpiDataItems, kpiDataLoading, kpiDataError,
    expandedRows, setExpandedRows,
    expandedDefaultRows, setExpandedDefaultRows,
    isInfra, filteredStatesList,
    infraPivotedSchools, infraDefaultColumns,
    allKpiSchools, filteredAllKpiSchools, visibleGroups,
    categoryConfig,
  } = kpiData;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Status text */}
      <div className="mb-4 flex flex-col gap-2">
        {!category && !kpi && !state && !region && <p className="text-sm text-gray-500">Showing all records</p>}
        {category && kpi && (
          <p className="text-sm text-green-600">
            Filtered by: {categoryList.find(c => c.key === category)?.label} - {kpiList.find(k => k.key === kpi)?.label}
          </p>
        )}
        {(state || region) && (
          <p className="text-sm text-blue-600">
            {state && `State: ${state}`}
            {state && region && ' | '}
            {region && `Region: ${region}`}
          </p>
        )}
        {globalFilters.length > 0 && <p className="text-sm text-purple-600">Active filters: {globalFilters.length}</p>}
      </div>

      {/* Loading */}
      {kpiDataLoading && (
        <div className="flex flex-col items-center justify-center h-[300px] bg-gray-50 rounded-lg">
          <KVSLoader size={60} color="#6B1786" />
          <p className="text-gray-500 mt-4">Loading table data...</p>
        </div>
      )}

      {/* Error */}
      {kpiDataError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{kpiDataError}</p>
        </div>
      )}

      {/* No records */}
      {!kpiDataLoading && !kpiDataError && category && kpi && globalFilters.length > 0 && visibleGroups.length === 0 && (
        <div className="flex items-center justify-center min-h-[400px] bg-gray-50 rounded-lg">
          <div className="text-center p-12">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No schools match the current filter criteria.</h3>
            <p className="text-sm text-gray-400">Try adjusting your filters to see table data.</p>
          </div>
        </div>
      )}

      {/* Table content */}
      {!kpiDataLoading && !kpiDataError && (!category || !kpi || !globalFilters.length || visibleGroups.length > 0) && (
        <div className="overflow-x-auto">
          {category && kpi && kpiDataItems.length > 0 ? (
            <KPITable
              kpiDataItems={kpiDataItems}
              visibleGroups={visibleGroups}
              globalFilters={globalFilters}
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
              category={category}
              kpi={kpi}
              kpiList={kpiList}
              activeQuickFilters={activeQuickFilters}
              isInfra={isInfra}
              infraPivotedSchools={infraPivotedSchools}
              infraDefaultColumns={infraDefaultColumns}
              allKpiSchools={allKpiSchools}
              filteredAllKpiSchools={filteredAllKpiSchools}
              categoryConfig={categoryConfig}
            />
          ) : (
            <DefaultTable
              filteredStatesList={filteredStatesList}
              expandedDefaultRows={expandedDefaultRows}
              setExpandedDefaultRows={setExpandedDefaultRows}
              category={category}
              kpi={kpi}
              kpiList={kpiList}
              activeQuickFilters={activeQuickFilters}
              isInfra={isInfra}
              infraPivotedSchools={infraPivotedSchools}
              infraDefaultColumns={infraDefaultColumns}
              categoryConfig={categoryConfig}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// KPITable — when both category and KPI are selected
// ═══════════════════════════════════════════════════════════════

interface KPITableProps {
  kpiDataItems: any[];
  visibleGroups: any[];
  globalFilters: any[];
  expandedRows: Set<number>;
  setExpandedRows: (v: Set<number>) => void;
  category: string;
  kpi: string;
  kpiList: Array<{ key: string; label: string }>;
  activeQuickFilters: string[];
  isInfra: boolean;
  infraPivotedSchools: any[];
  infraDefaultColumns: string[];
  allKpiSchools: any[];
  filteredAllKpiSchools: any[];
  categoryConfig: CategoryConfig;
}

function KPITable({
  kpiDataItems, visibleGroups, globalFilters,
  expandedRows, setExpandedRows,
  category, kpi, kpiList, activeQuickFilters,
  isInfra, infraPivotedSchools, infraDefaultColumns,
  allKpiSchools, filteredAllKpiSchools,
  categoryConfig,
}: KPITableProps) {
  const groups = visibleGroups;

  // ── Single group → show flat table ────────────────────────
  if (groups.length === 1) {
    const g = groups[0];
    const item = g.item;
    const groupingKey = Object.keys(item).find(k =>
      !['count', 'School Count', 'kvs_details', 'roID', 'stateID', 'districtID', 'roName', 'stateName', 'districtName', 'city'].includes(k)
    );
    let kpiValue = item.roName || item.stateName || item.districtName || item.city || '';
    if (!kpiValue && groupingKey) kpiValue = item[groupingKey];

    return (
      <div className="p-6 bg-gray-50">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-semibold text-gray-800 text-base">
            {formatCellValue(kpiValue, groupingKey || '')}
            <span className="text-sm font-normal text-gray-600 ml-2">({g.filteredSchools.length} records)</span>
          </h4>
          {globalFilters.length > 0 && g.filteredSchools.length === 0 && (
            <div className="text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded">No records match the current filters</div>
          )}
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm overflow-hidden">
          <SchoolDetailsTable
            schools={isInfra ? infraPivotedSchools : g.filteredSchools}
            formatCellValue={formatCellValue}
            maxHeight={700}
            selectedCategory={category}
            selectedKPI={kpi}
            selectedKPILabel={isInfra ? 'Infrastructure Comparison' : kpiList.find(k => k.key === kpi)?.label}
            columnLabelOverrides={categoryConfig.columns?.labelOverrides}
            hiddenColumns={categoryConfig.columns?.hideColumns}
            defaultVisibleColumns={
              isInfra ? infraDefaultColumns : [
                ...(categoryConfig.columns?.defaultVisibleColumns ?? ['schoolName', 'stateName', 'roName', 'districtName']),
                ...(activeQuickFilters.includes('pm-shri') ? ['isPMShree'] : []),
              ]
            }
          />
        </div>
      </div>
    );
  }

  // ── Multi-group → expandable table ────────────────────────
  const toggleRow = (idx: number) => {
    const next = new Set(expandedRows);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    setExpandedRows(next);
  };

  const firstItem = kpiDataItems[0];
  const headerLabel = firstItem
    ? firstItem.roName ? 'RO Name'
    : firstItem.stateName ? 'State Name'
    : firstItem.districtName ? 'District Name'
    : firstItem.city ? 'City'
    : kpiList.find(k => k.key === kpi)?.label || kpi
    : 'Name';

  return (
    <table className="min-w-full bg-white border border-gray-200 rounded-lg" style={{ tableLayout: 'fixed', width: '100%' }}>
      <thead>
        <tr className="bg-gray-50">
          <th className="px-4 py-3 border-b text-left font-semibold text-gray-700" style={{ width: '80px' }}>S.No.</th>
          <th className="px-4 py-3 border-b text-left font-semibold text-gray-700" style={{ width: '100px' }}>Expand</th>
          <th className="px-4 py-3 border-b text-left font-semibold text-gray-700">{headerLabel}</th>
          <th className="px-4 py-3 border-b text-left font-semibold text-gray-700" style={{ width: '150px' }}>{isInfra ? 'Schools' : 'Records'}</th>
        </tr>
      </thead>
      <tbody>
        {/* "All" row */}
        <AllRow
          expandedRows={expandedRows}
          toggleRow={toggleRow}
          isInfra={isInfra}
          infraPivotedSchools={infraPivotedSchools}
          infraDefaultColumns={infraDefaultColumns}
          allKpiSchools={allKpiSchools}
          filteredAllKpiSchools={filteredAllKpiSchools}
          globalFilters={globalFilters}
          category={category}
          kpi={kpi}
          kpiList={kpiList}
          activeQuickFilters={activeQuickFilters}
          categoryConfig={categoryConfig}
        />

        {/* Individual rows */}
        {kpiDataItems
          .filter(item => {
            if (kpi && item[kpi] === null) return false;
            if (globalFilters.length > 0) {
              const raw = Array.isArray(item.kvs_details) ? item.kvs_details : [];
              const rawFilters = isInfra ? globalFilters.filter(f => !f.field.startsWith('infra_')) : globalFilters;
              return applyFilters(raw, rawFilters).length > 0;
            }
            return true;
          })
          .map((item, index) => {
            const raw = Array.isArray(item.kvs_details) ? item.kvs_details : [];
            const rawFilters = isInfra ? globalFilters.filter(f => !f.field.startsWith('infra_')) : globalFilters;
            const filteredSchools = applyFilters(raw, rawFilters);

            const groupInfra = isInfra
              ? infraPivotedSchools.filter(ps => {
                  if (item.roName) return ps.roName === item.roName;
                  if (item.stateName) return ps.stateName === item.stateName;
                  if (item.districtName) return ps.districtName === item.districtName;
                  if (item.city) return ps.city === item.city;
                  return true;
                })
              : [];

            const effectiveCount = isInfra ? groupInfra.length : filteredSchools.length;
            if (effectiveCount === 0) return null;

            const groupingKey = Object.keys(item).find(k =>
              !['count', 'School Count', 'kvs_details', 'roID', 'stateID', 'districtID', 'roName', 'stateName', 'districtName', 'city'].includes(k)
            );
            let kpiValue = item.roName || item.stateName || item.districtName || item.city || '';
            let displayField = '';
            if (!kpiValue && groupingKey) { kpiValue = item[groupingKey]; displayField = groupingKey; }
            else {
              if (item.roName) displayField = 'roName';
              else if (item.stateName) displayField = 'stateName';
              else if (item.districtName) displayField = 'districtName';
              else if (item.city) displayField = 'city';
            }

            const isExpanded = expandedRows.has(index);

            return (
              <React.Fragment key={index}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleRow(index)}>
                  <td className="px-4 py-3 border-b text-center">{index + 1}</td>
                  <td className="px-4 py-3 border-b">
                    <button className="text-blue-600 hover:text-blue-800">{isExpanded ? '▼' : '▶'}</button>
                  </td>
                  <td className="px-4 py-3 border-b font-medium">{formatCellValue(kpiValue, displayField)}</td>
                  <td className="px-4 py-3 border-b">{effectiveCount}</td>
                </tr>

                {isExpanded && effectiveCount > 0 && (
                  <tr>
                    <td colSpan={4} className="p-6 bg-gray-50">
                      <ExpandedGroup
                        filteredSchools={filteredSchools}
                        rawCount={raw.length}
                        globalFilters={globalFilters}
                        isInfra={isInfra}
                        groupInfra={groupInfra}
                        infraDefaultColumns={infraDefaultColumns}
                        category={category}
                        kpi={kpi}
                        kpiList={kpiList}
                        activeQuickFilters={activeQuickFilters}
                        categoryConfig={categoryConfig}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
      </tbody>
    </table>
  );
}

// ─── "All" row ───────────────────────────────────────────────

interface AllRowProps {
  expandedRows: Set<number>;
  toggleRow: (idx: number) => void;
  isInfra: boolean;
  infraPivotedSchools: any[];
  infraDefaultColumns: string[];
  allKpiSchools: any[];
  filteredAllKpiSchools: any[];
  globalFilters: any[];
  category: string;
  kpi: string;
  kpiList: Array<{ key: string; label: string }>;
  activeQuickFilters: string[];
  categoryConfig: CategoryConfig;
}

function AllRow({
  expandedRows, toggleRow,
  isInfra, infraPivotedSchools, infraDefaultColumns,
  allKpiSchools, filteredAllKpiSchools, globalFilters,
  category, kpi, kpiList, activeQuickFilters,
  categoryConfig,
}: AllRowProps) {
  const displayCount = isInfra ? infraPivotedSchools.length : filteredAllKpiSchools.length;
  if (displayCount === 0) return null;

  const isExpanded = expandedRows.has(-1);
  const totalCount = allKpiSchools.length;

  return (
    <React.Fragment>
      <tr className="hover:bg-gray-50 cursor-pointer bg-blue-50" onClick={() => toggleRow(-1)}>
        <td className="px-4 py-3 border-b text-center font-semibold">-</td>
        <td className="px-4 py-3 border-b">
          <button className="text-blue-600 hover:text-blue-800 font-semibold">{isExpanded ? '▼' : '▶'}</button>
        </td>
        <td className="px-4 py-3 border-b font-bold text-blue-700">All</td>
        <td className="px-4 py-3 border-b font-semibold">{displayCount}</td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={4} className="p-6 bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold text-gray-800 text-base">
                All School Details
                {globalFilters.length > 0 ? (
                  <span className="text-sm font-normal text-blue-600 ml-2">({displayCount} of {totalCount} schools match filters)</span>
                ) : (
                  <span className="text-sm font-normal text-gray-600 ml-2">({totalCount} schools)</span>
                )}
              </h4>
              {globalFilters.length > 0 && displayCount === 0 && (
                <div className="text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded">No schools match the current filters</div>
              )}
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm overflow-hidden">
              <SchoolDetailsTable
                schools={isInfra ? infraPivotedSchools : filteredAllKpiSchools}
                formatCellValue={formatCellValue}
                maxHeight={700}
                selectedCategory={category}
                selectedKPI={kpi}
                selectedKPILabel={isInfra ? 'Infrastructure Comparison' : kpiList.find(k => k.key === kpi)?.label}
                columnLabelOverrides={categoryConfig.columns?.labelOverrides}
                hiddenColumns={categoryConfig.columns?.hideColumns}
                defaultVisibleColumns={
                  isInfra ? infraDefaultColumns : [
                    ...(categoryConfig.columns?.defaultVisibleColumns ?? [
                      'schoolName', 'stateName', 'roName', 'districtName', 'district',
                      'schoolDetails.schoolName', 'schoolDetails.stateName', 'schoolDetails.roName', 'schoolDetails.districtName',
                    ]),
                    ...(activeQuickFilters.includes('pm-shri') ? ['isPMShree', 'schoolDetails.isPMShree'] : []),
                  ]
                }
              />
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

// ─── Expanded group inside individual rows ───────────────────

interface ExpandedGroupProps {
  filteredSchools: any[];
  rawCount: number;
  globalFilters: any[];
  isInfra: boolean;
  groupInfra: any[];
  infraDefaultColumns: string[];
  category: string;
  kpi: string;
  kpiList: Array<{ key: string; label: string }>;
  activeQuickFilters: string[];
  categoryConfig: CategoryConfig;
}

function ExpandedGroup({
  filteredSchools, rawCount, globalFilters,
  isInfra, groupInfra, infraDefaultColumns,
  category, kpi, kpiList, activeQuickFilters,
  categoryConfig,
}: ExpandedGroupProps) {
  const displayCount = isInfra ? groupInfra.length : filteredSchools.length;
  const actualKpiField = kpi || '';

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-semibold text-gray-800 text-base">
          School Details
          {globalFilters.length > 0 ? (
            <span className="text-sm font-normal text-blue-600 ml-2">({displayCount} of {rawCount} schools match filters)</span>
          ) : (
            <span className="text-sm font-normal text-gray-600 ml-2">({rawCount} schools)</span>
          )}
        </h4>
        {globalFilters.length > 0 && displayCount === 0 && (
          <div className="text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded">No schools match the current filters</div>
        )}
      </div>
      <div className="bg-white rounded-lg p-4 shadow-sm overflow-hidden">
        <SchoolDetailsTable
          schools={isInfra ? groupInfra : filteredSchools}
          formatCellValue={formatCellValue}
          maxHeight={700}
          selectedCategory={category}
          selectedKPI={kpi}
          selectedKPILabel={isInfra ? 'Infrastructure Comparison' : kpiList.find(k => k.key === kpi)?.label}
          columnLabelOverrides={categoryConfig.columns?.labelOverrides}
          hiddenColumns={categoryConfig.columns?.hideColumns}
          defaultVisibleColumns={
            isInfra ? infraDefaultColumns :
            (() => {
              const defaults = [
                ...(categoryConfig.columns?.defaultVisibleColumns ?? [
                  'schoolName', 'stateName', 'roName', 'districtName', 'district',
                  'schoolDetails.schoolName', 'schoolDetails.stateName', 'schoolDetails.roName', 'schoolDetails.districtName',
                ]),
              ];
              if (actualKpiField && actualKpiField.trim() !== '' && !defaults.includes(actualKpiField)) {
                defaults.push(actualKpiField);
              }
              return Array.from(new Set(defaults));
            })()
          }
        />
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// DefaultTable — when no KPI is selected, show state-level data
// ═══════════════════════════════════════════════════════════════

interface DefaultTableProps {
  filteredStatesList: any[];
  expandedDefaultRows: Set<number>;
  setExpandedDefaultRows: (v: Set<number>) => void;
  category: string;
  kpi: string;
  kpiList: Array<{ key: string; label: string }>;
  activeQuickFilters: string[];
  isInfra: boolean;
  infraPivotedSchools: any[];
  infraDefaultColumns: string[];
  categoryConfig: CategoryConfig;
}

function DefaultTable({
  filteredStatesList, expandedDefaultRows, setExpandedDefaultRows,
  category, kpi, kpiList, activeQuickFilters,
  isInfra, infraPivotedSchools, infraDefaultColumns,
  categoryConfig,
}: DefaultTableProps) {
  if (filteredStatesList.length === 0) {
    return <div className="text-center py-8 text-gray-400">No data available</div>;
  }

  const toggle = (idx: number) => {
    const next = new Set(expandedDefaultRows);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    setExpandedDefaultRows(next);
  };

  return (
    <table className="min-w-full bg-white border border-gray-200 rounded-lg">
      <thead>
        <tr className="bg-gray-50">
          <th className="px-4 py-3 border-b text-left font-semibold text-gray-700" style={{ width: '80px' }}>S.No.</th>
          <th className="px-4 py-3 border-b text-left font-semibold text-gray-700" style={{ width: '100px' }}>Expand</th>
          <th className="px-4 py-3 border-b text-left font-semibold text-gray-700">State Name</th>
          <th className="px-4 py-3 border-b text-left font-semibold text-gray-700" style={{ width: '150px' }}>Schools</th>
        </tr>
      </thead>
      <tbody>
        {filteredStatesList
          .filter(s => s.kvisDetails && s.kvisDetails.length > 0)
          .map((stateItem, index) => {
            const isExpanded = expandedDefaultRows.has(index);
            const hasSchools = stateItem.kvisDetails && stateItem.kvisDetails.length > 0;

            return (
              <React.Fragment key={stateItem.stateID || index}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => hasSchools && toggle(index)}>
                  <td className="px-4 py-3 border-b text-center">{index + 1}</td>
                  <td className="px-4 py-3 border-b">
                    {hasSchools
                      ? <button className="text-blue-600 hover:text-blue-800">{isExpanded ? '▼' : '▶'}</button>
                      : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 border-b font-medium">{stateItem.stateName}</td>
                  <td className="px-4 py-3 border-b">{stateItem.schoolCount}</td>
                </tr>

                {isExpanded && hasSchools && stateItem.kvisDetails && (
                  <tr>
                    <td colSpan={4} className="p-6 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-4 text-base">
                        School Details ({stateItem.kvisDetails.length} schools)
                      </h4>
                      <div className="bg-white rounded-lg p-4 shadow-sm overflow-hidden">
                        <SchoolDetailsTable
                          schools={isInfra ? infraPivotedSchools : stateItem.kvisDetails}
                          formatCellValue={formatCellValue}
                          maxHeight={700}
                          selectedCategory={category}
                          selectedKPI={kpi}
                          selectedKPILabel={isInfra ? 'Infrastructure Comparison' : kpiList.find(k => k.key === kpi)?.label}
                          columnLabelOverrides={categoryConfig.columns?.labelOverrides}
                          hiddenColumns={categoryConfig.columns?.hideColumns}
                          defaultVisibleColumns={
                            isInfra ? infraDefaultColumns : [
                              ...(categoryConfig.columns?.defaultVisibleColumns ?? ['schoolName', 'stateName', 'roName', 'districtName']),
                              ...(activeQuickFilters.includes('pm-shri') ? ['isPMShree'] : []),
                            ]
                          }
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
      </tbody>
    </table>
  );
}
